import { StatusBar } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';

class StatusBarService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
  }

  /**
   * Initialize status bar with theme colors
   */
  async initialize(theme) {
    try {
      if (this.isNative) {
        // Use Capacitor plugin for native platforms
        await this.setStatusBarStyle(theme);
      } else {
        // Use CSS meta tags for web/PWA
        this.setWebStatusBarStyle(theme);
      }
    } catch (error) {
      console.error('StatusBar: Initialization failed', error);
    }
  }

  /**
   * Force update status bar with current theme
   */
  async forceUpdate(theme) {
    try {
      if (this.isNative) {
        await this.setStatusBarStyle(theme);
      } else {
        this.setWebStatusBarStyle(theme);
      }
    } catch (error) {
      console.error('StatusBar: Force update failed', error);
    }
  }

  /**
   * Set status bar style for native platforms using Capacitor
   */
  async setStatusBarStyle(theme) {
    try {
      const backgroundColor = theme.palette.background.default || '#F8F8FF';
      const style = this.getTextStyle(backgroundColor);

      await StatusBar.setStyle({ style });
      await StatusBar.setBackgroundColor({ color: backgroundColor });
      
      console.log('StatusBar: Set to', backgroundColor, 'with', style, 'text');
    } catch (error) {
      console.error('StatusBar: Failed to set style', error);
    }
  }

  /**
   * Set status bar style for web/PWA using CSS meta tags
   */
  setWebStatusBarStyle(theme) {
    try {
      const backgroundColor = theme.palette.background.default || '#F8F8FF';
      const style = this.getTextStyle(backgroundColor);

      // Update theme-color meta tag
      let themeColorMeta = document.querySelector('meta[name="theme-color"]');
      if (!themeColorMeta) {
        themeColorMeta = document.createElement('meta');
        themeColorMeta.name = 'theme-color';
        document.head.appendChild(themeColorMeta);
      }
      themeColorMeta.content = backgroundColor;

      // Update apple-mobile-web-app-status-bar-style
      let appleStatusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
      if (!appleStatusBarMeta) {
        appleStatusBarMeta = document.createElement('meta');
        appleStatusBarMeta.name = 'apple-mobile-web-app-status-bar-style';
        document.head.appendChild(appleStatusBarMeta);
      }
      appleStatusBarMeta.content = style === 'dark' ? 'black-translucent' : 'default';

      console.log('Web StatusBar: Set to', backgroundColor, 'with', style, 'text');
    } catch (error) {
      console.error('Web StatusBar: Failed to set style', error);
    }
  }

  /**
   * Determine text style based on background color brightness
   */
  getTextStyle(backgroundColor) {
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? 'dark' : 'light';
  }

  /**
   * Show/Hide status bar
   */
  async show() {
    if (this.isNative) {
      try { await StatusBar.show(); } catch (error) { console.error('StatusBar: Failed to show', error); }
    }
  }

  async hide() {
    if (this.isNative) {
      try { await StatusBar.hide(); } catch (error) { console.error('StatusBar: Failed to hide', error); }
    }
  }

  /**
   * Set status bar overlay
   */
  async setOverlay(overlay = false) {
    if (this.isNative) {
      try { await StatusBar.setOverlaysWebView({ overlay }); } catch (error) { console.error('StatusBar: Failed to set overlay', error); }
    }
  }
}

export default new StatusBarService(); 